import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { HelpArticleListItem, HomeCopyConfig } from "@/lib/quick-actions";
import {
  HelpIndexSkeleton,
  HelpListSkeleton,
  SkeletonStyles,
  contentInStyle,
} from "./Skeletons";

/**
 * The "Help" page (Intercom parity): back + centred title header, a big
 * always-visible search bar, then the org's help library — grouped into
 * collections when articles carry collection metadata ("4 collections" →
 * drill into a collection → its articles, with optional section headings),
 * or a flat "N articles" list when none do. Search covers EVERY published
 * article (title/description/body via content_text) from anywhere.
 *
 * Reached from the home screen's ?-chip and by clicking the home search
 * input (which no longer types in place — it opens this page, like
 * Intercom). Renders on white like every non-home view.
 */

interface HelpIndexViewProps {
  organizationId: string;
  onBack: () => void;
  /** Open an article (the widget auto-enlarges — wired in ChatWidget). */
  onOpenArticle: (articleId: string) => void;
  /** Focus the search box on mount (the home input-click path). */
  autoFocusSearch?: boolean;
  /** Search placeholder — the quick-actions card title (locale-aware). */
  searchPlaceholder?: string;
  /** Locale copy: help_title + count labels + no-results line. */
  homeCopy?: HomeCopyConfig | null;
  enableAnimations?: boolean;
}

interface CollectionGroup {
  title: string;
  description?: string;
  rank: number;
  articles: HelpArticleListItem[];
}

const or = (v: string | undefined, fallback: string) =>
  (v || "").trim() || fallback;

const ArticleRow: React.FC<{
  item: { id: string; title: string; description?: string | null };
  onOpen: (id: string) => void;
}> = ({ item, onOpen }) => (
  <button
    onClick={() => onOpen(item.id)}
    className="w-full py-3.5 flex items-center justify-between gap-3 text-left group border-b border-gray-50 last:border-b-0"
  >
    <div className="flex-1 min-w-0">
      <p
        className="text-[15px] text-gray-800 group-hover:text-gray-900 font-medium"
        style={{ transition: "color 200ms ease-out" }}
      >
        {item.title}
      </p>
      {item.description && (
        <p className="text-[13px] text-gray-400 truncate mt-0.5">
          {item.description}
        </p>
      )}
    </div>
    <ChevronRight
      className="text-gray-300 w-4 h-4 flex-shrink-0 group-hover:text-gray-500"
      style={{ transition: "color 200ms ease-out" }}
    />
  </button>
);

const HelpIndexView: React.FC<HelpIndexViewProps> = ({
  organizationId,
  onBack,
  onOpenArticle,
  autoFocusSearch = false,
  searchPlaceholder,
  homeCopy,
  enableAnimations = true,
}) => {
  const [articles, setArticles] = useState<HelpArticleListItem[] | null>(null);
  const [openCollection, setOpenCollection] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<
    { id: string; title: string; description: string | null }[] | null
  >(null);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Locale copy (defaults English). Singular falls back to plural-minus-"s"
  // where that works; languages where it doesn't set the explicit singular.
  const title = or(homeCopy?.help_title, "Help");
  const placeholder = or(searchPlaceholder, "Search for help");
  const collectionsPlural = or(homeCopy?.help_collections_label, "collections");
  const collectionsSingular = or(
    homeCopy?.help_collection_singular,
    collectionsPlural.endsWith("s") ? collectionsPlural.slice(0, -1) : collectionsPlural,
  );
  const articlesPlural = or(homeCopy?.help_articles_label, "articles");
  const articlesSingular = or(
    homeCopy?.help_article_singular,
    articlesPlural.endsWith("s") ? articlesPlural.slice(0, -1) : articlesPlural,
  );
  const noResults = or(homeCopy?.help_no_results, "No results — try a different word.");
  const countLabel = (n: number, plural: string, singular: string) =>
    `${n} ${n === 1 ? singular : plural}`;

  // The library (one fetch per open — RLS exposes published rows only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("widget_help_articles" as any)
        .select("id, title, description, collection")
        .eq("organization_id", organizationId)
        .order("title");
      if (!cancelled) setArticles(((data as any) || []) as HelpArticleListItem[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Autofocus (home input-click path). Desktop gets the caret immediately;
  // iOS only opens the keyboard for in-gesture focus, so there the page
  // simply opens with the bar ready to tap.
  useEffect(() => {
    if (!autoFocusSearch) return;
    const raf = requestAnimationFrame(() =>
      inputRef.current?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Full-library search over the content_text haystack, debounced. Previous
  // results stay on screen while a refinement is in flight (no flicker).
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setResults(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      const safe = q.replace(/[%_]/g, "\\$&");
      const { data, error } = await supabase
        .from("widget_help_articles" as any)
        .select("id, title, description")
        .eq("organization_id", organizationId)
        .ilike("content_text", `%${safe}%`)
        .limit(10);
      if (cancelled) return;
      setResults(error ? [] : ((data as any) || []));
      setSearching(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, organizationId]);

  const groups = useMemo<CollectionGroup[] | null>(() => {
    if (!articles) return null;
    const map = new Map<string, CollectionGroup>();
    for (const a of articles) {
      const c = a.collection;
      if (!c || !c.title) continue;
      let g = map.get(c.title);
      if (!g) {
        g = {
          title: c.title,
          description: c.description,
          rank: typeof c.rank === "number" ? c.rank : 999,
          articles: [],
        };
        map.set(c.title, g);
      }
      if (!g.description && c.description) g.description = c.description;
      if (typeof c.rank === "number" && c.rank < g.rank) g.rank = c.rank;
      g.articles.push(a);
    }
    return [...map.values()].sort(
      (a, b) => a.rank - b.rank || a.title.localeCompare(b.title),
    );
  }, [articles]);
  const uncollected = useMemo(
    () => (articles || []).filter((a) => !a.collection?.title),
    [articles],
  );

  const searchActive = query.trim().length > 0;
  const activeGroup =
    (openCollection && groups?.find((g) => g.title === openCollection)) || null;

  // Collection page: articles grouped by optional section, sections in
  // first-appearance order, section-less articles first.
  const activeSections = useMemo(() => {
    if (!activeGroup) return [];
    const out: { section: string | null; items: HelpArticleListItem[] }[] = [];
    const byKey = new Map<string | null, HelpArticleListItem[]>();
    const keyOrder: (string | null)[] = [];
    for (const a of activeGroup.articles) {
      const key = a.collection?.section?.trim() || null;
      if (!byKey.has(key)) {
        byKey.set(key, []);
        keyOrder.push(key);
      }
      byKey.get(key)!.push(a);
    }
    keyOrder.sort((a, b) => (a === null ? -1 : b === null ? 1 : 0));
    for (const key of keyOrder) out.push({ section: key, items: byKey.get(key)! });
    return out;
  }, [activeGroup]);

  const paneKey = searchActive
    ? "search"
    : activeGroup
      ? `c-${activeGroup.title}`
      : "index";

  const indexPane = (
    <div className="px-5 pb-6" style={contentInStyle}>
      {groups && groups.length > 0 ? (
        <>
          <h3 className="text-[17px] font-bold text-gray-900 pt-3 pb-1">
            {countLabel(groups.length, collectionsPlural, collectionsSingular)}
          </h3>
          <div>
            {groups.map((g) => (
              <button
                key={g.title}
                onClick={() => setOpenCollection(g.title)}
                className="w-full py-4 flex items-center justify-between gap-3 text-left group border-b border-gray-100 last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[16px] font-semibold text-gray-900">
                    {g.title}
                  </p>
                  {g.description && (
                    <p className="text-[14px] text-gray-500 mt-0.5">
                      {g.description}
                    </p>
                  )}
                  <p className="text-[13px] text-gray-400 mt-1">
                    {countLabel(g.articles.length, articlesPlural, articlesSingular)}
                  </p>
                </div>
                <ChevronRight
                  className="text-gray-300 w-4 h-4 flex-shrink-0 group-hover:text-gray-500"
                  style={{ transition: "color 200ms ease-out" }}
                />
              </button>
            ))}
          </div>
          {uncollected.length > 0 && (
            <div className="mt-4">
              <h3 className="text-[17px] font-bold text-gray-900 pt-1 pb-1">
                {countLabel(uncollected.length, articlesPlural, articlesSingular)}
              </h3>
              {uncollected.map((a) => (
                <ArticleRow key={a.id} item={a} onOpen={onOpenArticle} />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <h3 className="text-[17px] font-bold text-gray-900 pt-3 pb-1">
            {countLabel(
              (articles || []).length,
              articlesPlural,
              articlesSingular,
            )}
          </h3>
          {(articles || []).map((a) => (
            <ArticleRow key={a.id} item={a} onOpen={onOpenArticle} />
          ))}
        </>
      )}
    </div>
  );

  const collectionPane = activeGroup && (
    <div className="px-5 pb-6" style={contentInStyle}>
      <h3 className="text-[20px] font-bold text-gray-900 pt-3 leading-snug">
        {activeGroup.title}
      </h3>
      {activeGroup.description && (
        <p className="text-[14px] text-gray-500 mt-1">
          {activeGroup.description}
        </p>
      )}
      <p className="text-[13px] text-gray-400 mt-1 mb-1">
        {countLabel(activeGroup.articles.length, articlesPlural, articlesSingular)}
      </p>
      {activeSections.map(({ section, items }) => (
        <div key={section || "_"}>
          {section && (
            <p className="text-[12px] font-semibold uppercase tracking-wider text-gray-400 mt-5 mb-0.5">
              {section}
            </p>
          )}
          {items.map((a) => (
            <ArticleRow key={a.id} item={a} onOpen={onOpenArticle} />
          ))}
        </div>
      ))}
    </div>
  );

  const searchPane = (
    <div className="pb-6">
      {results === null ? (
        <HelpListSkeleton rows={5} />
      ) : results.length > 0 ? (
        <div className="px-5" style={contentInStyle}>
          {results.map((r) => (
            <ArticleRow key={r.id} item={r} onOpen={onOpenArticle} />
          ))}
        </div>
      ) : searching ? (
        <HelpListSkeleton rows={3} />
      ) : (
        <p className="px-5 py-6 text-gray-400 text-[14px]" style={contentInStyle}>
          {noResults}
        </p>
      )}
    </div>
  );

  return (
    <div
      className="h-full flex flex-col bg-white"
      style={{ fontSize: "16px" }}
    >
      <SkeletonStyles />

      {/* Header: back left, centred title. The permanent top-right chrome
          (enlarge on desktop / X on mobile) floats over this row. */}
      <div className="relative flex items-center px-3 pt-3 pb-1 flex-shrink-0 h-[52px]">
        <button
          onClick={() => (activeGroup ? setOpenCollection(null) : onBack())}
          aria-label="Back"
          className="h-9 w-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="absolute left-1/2 -translate-x-1/2 text-[17px] font-bold text-gray-900 truncate max-w-[55%] text-center pointer-events-none">
          {title}
        </h2>
      </div>

      {/* Search — always visible, searches the whole library from anywhere */}
      <div className="px-4 pt-1.5 pb-3 flex-shrink-0">
        <div className="flex items-center gap-3 bg-gray-100 rounded-2xl px-4 transition-all duration-200 focus-within:bg-gray-50 focus-within:ring-2 focus-within:ring-black/10">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
            className="flex-1 min-w-0 bg-transparent outline-none border-none py-3.5 text-[16px] md:text-[15px] text-gray-900 placeholder:text-gray-400"
          />
          <Search className="w-5 h-5 text-gray-500 flex-shrink-0" />
        </div>
      </div>

      {/* Body — index / collection / search panes. Each pane carries the
          fade-up entrance via contentInStyle; keying on the pane identity
          remounts it so the entrance replays on every pane switch AND on the
          skeleton→content hand-off (one animation system, no double-fade). */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {articles === null && !searchActive ? (
          <HelpIndexSkeleton />
        ) : (
          <div key={paneKey}>
            {searchActive ? searchPane : activeGroup ? collectionPane : indexPane}
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpIndexView;
